/**
 * Privacy Policy, English version.
 *
 * Written from the real database schema and the calls the server makes, not
 * from a template. If what we collect or where it goes changes, this file
 * changes with it — and the date goes up.
 */
export default {
    titulo: 'Privacy Policy',
    actualizado: 'September 10, 2026',

    intro: [
        'ShippingFULL is a dropshipping tool operated from New Jersey, United States. This policy explains what data we collect, why, who we share it with, and what you can do about it.',
        'One thing is worth understanding up front, because it shapes everything else: this application handles two very different kinds of people. There are **our users** — you, who open an account — and there are **your users\u2019 buyers**, who have never heard of us and whose data reaches us because you connected your store.'
    ],

    secciones: [
        {
            titulo: '1. What we collect from you, as a user',
            parrafos: [
                'When you create an account we store your **email address**, your **name**, and a **cryptographic hash of your password** — never the password itself, so not even we can read it.',
                'If you sign in with Google, we also store the identifier Google gives us and the address of your profile picture. We never receive your Google password.',
                'As you use the application we record your last sign-in time, your subscription tier, and a Stripe customer identifier if you take a paid plan.',
                'When you request a password reset we store a temporary token that **expires after 30 minutes** and is deleted as soon as it is used.'
            ]
        },
        {
            titulo: '2. Third-party data that passes through here',
            parrafos: [
                'When you connect a store and sync your sales, we receive your buyers\u2019 details from that platform: **name, email address, phone number, shipping address and billing address**, along with what they bought and for how much.',
                'Those people are not our users and have not accepted this policy. We act as a **processor on your behalf**: we store their data so you can manage and fulfil your orders, and we use it for nothing else. **You are the one answerable to your buyers** for how their data is handled.',
                'We do not sell that data, do not pass it to advertisers, do not use it to profile anyone, and do not send it to any artificial intelligence system.'
            ]
        },
        {
            titulo: '3. Your store credentials',
            parrafos: [
                'When you connect a store we store the credentials that platform authorises — keys and access tokens — **encrypted**, with a key that lives only in the server configuration and never in the source code.',
                'We use them only for what you ask: publishing a product, checking a listing\u2019s status, withdrawing it, or reading your sales. You can disconnect a store at any time and its credentials stop working.'
            ]
        },
        {
            titulo: '4. Who we share data with, and why',
            parrafos: [
                'We do not sell personal data. We share it only with the providers the application needs to work:',
                '**Supabase** (United States) hosts the database. **Render** runs the server. **Vercel** serves the interface.',
                '**Stripe** processes subscription payments. We never receive or store your card numbers: they go straight to Stripe.',
                '**Resend** delivers the emails the application sends, which today are only password resets.',
                '**eBay, CJ Dropshipping, Amazon and AliExpress** receive search queries and, when you publish, the product details. When fulfilling an order, the supplier receives the buyer\u2019s shipping address — without it there is no way to deliver the parcel.',
                '**Google (Gemini)** and **Anthropic (Claude)** analyse products and translate search terms. We have checked this line by line: **these services receive only product information** — title, description, price, rating, category — **and the term you type into the search box. No personal data, neither yours nor your buyers\u2019.**'
            ]
        },
        {
            titulo: '5. How long we keep it',
            parrafos: [
                'Your account data, for as long as the account exists. Orders and their buyer details, for as long as you keep the store connected or until you delete them.',
                'Password reset tokens expire after 30 minutes. Sessions expire after seven days. Search results are held in memory for one hour so we do not hammer the suppliers.',
                'If you close your account, write to us and we will delete your data. Note that data already held by eBay, Stripe or your supplier is governed by each of their policies, not ours.'
            ]
        },
        {
            titulo: '6. Your rights',
            parrafos: [
                'You can ask us for access to the data we hold about you, for corrections, for deletion, or for a copy in a readable format. Write to **support@shippingfull.com** and we will respond within a reasonable time.',
                'If you live in **New Jersey**, **California**, or another state with its own privacy law, you also have whatever rights that law grants you, including the right not to have your data sold — which we do not do in any case.',
                'If you live in the **European Union or the United Kingdom**: this application is operated from the United States and is not specifically designed to comply with the General Data Protection Regulation. If that regulation applies to you, take it into account before using the application or putting European buyers\u2019 data into it.'
            ]
        },
        {
            titulo: '7. Security, said honestly',
            parrafos: [
                'Passwords are stored as cryptographic hashes. Store credentials are encrypted. Traffic travels over a secure connection. Buyer data is not written to the server logs.',
                'That said: no system is invulnerable, and promising otherwise would be a lie. If there is ever a breach affecting your data, we will tell you.'
            ]
        },
        {
            titulo: '8. Minors',
            parrafos: [
                'ShippingFULL is a business tool and is not directed at anyone under 18. We do not knowingly collect data from minors.'
            ]
        },
        {
            titulo: '9. Changes',
            parrafos: [
                'If we change this policy, we will update the date in the header. If the change is significant, we will tell you inside the application.'
            ]
        },
        {
            titulo: '10. Contact',
            parrafos: [
                'For any privacy question: **support@shippingfull.com**.'
            ]
        }
    ]
};
